import { useContext, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { FastField, Formik } from 'formik';

import { BaseBox, BaseBoxHeading, BaseTextArea, RequestStepWrapper, CheckboxField } from 'bxcommon';
import { GeneralSummary } from './GeneralSummary';
import { ProspectusSummary } from '../Summary/ProspectusSummary';
import { InvoicingSummary } from '../Summary/InvoicingSummary';
import { BaseHint } from 'bxcommon';
import { RequestContext } from 'bxcommon/context/Request.context';
import { ModalContext } from 'bxcommon/context/Modal.context';
import { updateElectronicDelivery } from 'bxcommon/helpers/common.helpers';

import 'bxcommon/components/SummaryHelpers/SummaryHelpers.scss';
import { useValidation } from 'bxcommon/helpers/form.helper';
import { detailsInitialValidationSchema } from 'bxcommon/validations/RequestFormValidation';
import { FormWrapper } from 'bxcommon/components/FormWrapper/FormWrapper';

export const RequestFormSummary = (props) => {
  const { t: common } = useTranslation('common');
  const { t } = useTranslation();

  const { request, resources, updateRequestField, setCompletedState, formRef } = useContext(RequestContext);
  const { openConfirmModal } = useContext(ModalContext);

  const electronicDeliveryModal = (e) => {
    let result = updateElectronicDelivery(openConfirmModal, updateRequestField, t, { target: { checked: !e } });
    if (result === false) {
      formRef.current.setFieldValue('electronic_delivery_allowed', false, false);
    }
  };

  const contentText = (
    <>
      <p className="copy copy--small copy--bold">{t('nextSteps.message')}</p>
      <p className="copy copy--small">{t('nextSteps.review')}</p>
      <p className="copy copy--small">{t('nextSteps.processDays')}</p>
      <p className="copy copy--small">
        <Trans i18nKey="messages.furtherInformation" ns="common">
          <a
            href="https://www.regservices.ch/?download=6318"
            target="_blank"
            rel="noopener noreferrer"
            className="link--underlined"
          >
            Rules of Procedure.
          </a>
        </Trans>
      </p>
    </>
  );

  const initialValues = {
    comment: request.comment || '',
    electronic_delivery_allowed: Boolean(request.electronic_delivery_allowed) || true,
  };
  const [validationSchema, submitWithValidation] = useValidation(detailsInitialValidationSchema());

  useEffect(() => {
    setCompletedState(true);
  }, [setCompletedState]);

  return (
    <FormWrapper>
      <RequestStepWrapper
        isFinal
        content={contentText}
        formRef={formRef}
        validationSubmit={submitWithValidation}
        {...props}
      >
        <div className="base-summary__section-wrapper">
          <GeneralSummary isEquity={props.isEquity} {...props} request={request} resources={resources} step={0} />
          <ProspectusSummary {...props} request={request} resources={resources} step={1} />
          <InvoicingSummary {...props} request={request} resources={resources} step={2} />
          <BaseBox className="base-summary__comment" data-testid="summary-comment">
            <BaseBoxHeading>
              {common('request.summary.commentTitle')} {common('request.form.common.optional')}
            </BaseBoxHeading>
            <Formik
              initialValues={initialValues}
              innerRef={formRef}
              onSubmit={async (values) => {
                updateRequestField({ ...values });
              }}
              validationSchema={validationSchema}
            >
              {({ values }) => (
                <>
                  <div>
                    <div>{common('request.summary.commentBox')}</div>
                    <FastField
                      component={BaseTextArea}
                      name="comment"
                      value={values.comment}
                      data-testid="additional_certificates"
                    />
                  </div>
                  <div className="request-form--with-hint">
                    <CheckboxField
                      name="electronic_delivery_allowed"
                      checked={values.electronic_delivery_allowed}
                      onChange={electronicDeliveryModal}
                      text={common('request.summary.electronicDeliveryCheckbox')}
                    />
                    <p className="copy copy--small base-summary__fees">{t('request.summary.additionalFees')}</p>
                    <BaseHint>{t('hints.deliveryPlatform')}</BaseHint>
                  </div>
                </>
              )}
            </Formik>
          </BaseBox>
        </div>
      </RequestStepWrapper>
    </FormWrapper>
  );
};

export default RequestFormSummary;
